import { useAuth } from '../hooks/useAuth';
import { useCheckInHistory } from '../api/checkIns';
import { useCursorPagination } from '../hooks/useCursorPagination';
import PageHeader from '../components/layout/PageHeader';
import Button from '../components/ui/Button';
import { AlertTriangle, CalendarDays, ClipboardList } from 'lucide-react';
import { EmptyState } from '@/components/ui/empty-state';
import { CardListSkeleton } from '@/components/ui/skeleton';
import ErrorState from '../components/ui/ErrorState';
import { Card } from '@/components/ui/card';
import { formatDate } from '../lib/dates';

export default function CheckInHistoryPage() {
  const { currentUser } = useAuth();
  const { cursor, hasPrev, goNext, goPrev } = useCursorPagination();

  const { data, isLoading, error, refetch } = useCheckInHistory(currentUser!.id, cursor);

  if (isLoading) return <CardListSkeleton count={5} hasAction={false} />;
  if (error)     return <ErrorState onRetry={refetch} />;

  return (
    <div className="max-w-2xl">
      <PageHeader title="Check-in history" />

      {data?.items.length === 0 ? (
        <Card>
          <EmptyState icon={ClipboardList} title="No check-ins yet" description="Your daily check-ins will appear here." />
        </Card>
      ) : (
        <div className="space-y-3">
          {data?.items.map((ci) => (
            <Card key={ci.id} className="overflow-hidden">
              <div className="flex items-center gap-2.5 border-b border-border px-5 py-3">
                <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
                  <CalendarDays className="h-3.5 w-3.5 text-primary" />
                </div>
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{formatDate(ci.date)}</p>
              </div>
              <div className="divide-y divide-border">
                <div className="px-5 py-4">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Completed</p>
                  <p className="whitespace-pre-wrap text-sm text-foreground">{ci.completed}</p>
                </div>
                <div className="px-5 py-4">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wide text-muted-foreground">Planned next</p>
                  <p className="whitespace-pre-wrap text-sm text-foreground">{ci.plannedNext}</p>
                </div>
                {ci.blockers && (
                  <div className="px-5 py-4">
                    <p className="mb-1.5 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      <AlertTriangle className="h-3 w-3" /> Blockers
                    </p>
                    <p className="whitespace-pre-wrap text-sm text-amber-700 dark:text-amber-300">{ci.blockers}</p>
                  </div>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {(hasPrev || data?.hasMore) && (
        <div className="mt-4 flex justify-between">
          <Button variant="secondary" size="sm" disabled={!hasPrev} onClick={goPrev}>Previous</Button>
          <Button
            variant="secondary" size="sm"
            disabled={!data?.hasMore}
            onClick={() => data?.nextCursor && goNext(data.nextCursor)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
